import { IconButton, Typography } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import { GoogleOAuthProvider } from '@react-oauth/google';
import React, { useMemo, useState } from 'react';
import { ISqlEmployee } from '../../@types/modelV2/rights/user/sqlEmployee';
import { useHandleLinkGoogleAccount, useHandleUnlinkGoogleAccount } from '../../hooks/query/rights/userQueries';
import GeneralFunctions from '../../store/general/functions';
import GoogleSignInButton, { IGoogleLoginSuccess, IGoogleLoginError } from '../customComponentV2/button/GoogleSignInButton';
import PillButton from '../customComponentV2/button/PillButton';
import ConfirmationDialog from '../customComponentV2/dialog/ConfirmationDialog';
import ProjectDialog from '../customComponentV2/dialog/ProjectDialog';
import CustomTooltip from '../customComponentV2/tooltip/Tooltip';

interface IUserSettingsProps {
    isOpen : boolean;
    setIsUserDialogOpenCallback : (state : boolean) => void;
    currentUser : ISqlEmployee | null;
    onClose : () => void;
    signOut : () => void;
}

const UserSettings = (props : IUserSettingsProps) : React.ReactElement => {
    const { isLoading: isUnlinkingGoogleAccount, mutate: unlinkGoogleAccount } = useHandleUnlinkGoogleAccount();
    const { isLoading: isLinkingGoogleAccount, mutate: linkGoogleAccount } = useHandleLinkGoogleAccount();

    const [isUnlinkGoogleAccountDialogOpen, setIsUnlinkGoogleAccountDialogOpen] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const closeUserSettings = () : void => {
        props.onClose();
    };

    const onGoogleAccountLinkSuccess = async (response : IGoogleLoginSuccess) : Promise<void> => {
        linkGoogleAccount({ code: response.code });
    };

    const onGoogleAccountLinkFailure = (response : IGoogleLoginError) : void => {
        GeneralFunctions.generalShowErrorSnackbarV2(
            response.error ?? 'Google Account linking failed.',
            response,
        );
    };

    const onUnlinkGoogleAccount = () : void => {
        if (!props.currentUser)
            return;

        unlinkGoogleAccount();
        closeUnlinkGoogleAccountDialog();
    };

    const openUnlinkGoogleAccountDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(true);

    const closeUnlinkGoogleAccountDialog = () : void => setIsUnlinkGoogleAccountDialogOpen(false);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLinkingGoogleAccount
        || isUnlinkingGoogleAccount;
    }, [
        isLinkingGoogleAccount,
        isUnlinkingGoogleAccount
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <ProjectDialog
                title={'User Settings'}
                isOpen={props.isOpen}
                isLoadingCircular={isLoading}
                fullWidth
                maxWidth={'xs'}
                onClose={closeUserSettings}>
                <div className={'flx1 fdc aic jcc m10'}>
                    <div className={'fdc aic'}>
                        {
                            (!props.currentUser?.email || !props.currentUser.profilePictureURL)
                                && <div className={'zi1'}> 
                                    <GoogleOAuthProvider clientId={OAUTH_CLIENT_ID}>
                                        <GoogleSignInButton          
                                            onGoogleSignInSuccess={onGoogleAccountLinkSuccess}
                                            onGoogleSignInFailure={onGoogleAccountLinkFailure}   
                                            buttonText={`Link ${(!!props.currentUser?.email && !props.currentUser.profilePictureURL) ? 'Profile Picture' : 'Google Account'}`}   
                                        />
                                    </GoogleOAuthProvider>
                                </div>
                        }
                        {
                            !!props.currentUser?.email
                            && <div className={'fdc aic jcc'}>
                                <div className={'fdr aic jcc'}>
                                    <Typography className={'fdr m10'}>Linked Email:</Typography>
                                    <Typography variant={'body1'} className={'fdr fwb cpd m10'}>{ props.currentUser.email }</Typography>
                                    <CustomTooltip title={'Unlink Email'}>
                                        <IconButton className={'cr'} onClick={openUnlinkGoogleAccountDialog}>
                                            <ClearIcon />
                                        </IconButton>
                                    </CustomTooltip>
                                </div>
                            </div>
                        }
                    </div>
                    <div className={'fdr aic pb10 mt20'}>
                        <PillButton
                            className={'h35 w150'}
                            text={'LOGOUT'}
                            color={'secondary'}
                            onClick={props.signOut}
                        />
                    </div>
                </div>
            </ProjectDialog>
            <ConfirmationDialog 
                open={isUnlinkGoogleAccountDialogOpen}
                title={'Unlink Email'}
                description={'Are you sure you would like to unlink the email currently associated with your account?'}
                onAccept={onUnlinkGoogleAccount}
                onClose={closeUnlinkGoogleAccountDialog}
                dialogType={'orange'}
            />
        </>
    );
};

export default UserSettings;